import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Divider
} from '@mui/material';
import {
  School as TeacherIcon,
  FamilyRestroom as ParentIcon,
  SportsBasketball as CoachIcon,
  Person as GuardianIcon,
  ContactEmergency as EmergencyIcon,
  PersonRemove as UnlinkIcon
} from '@mui/icons-material';
import { getMyGuardians, unlinkGuardian } from '../../services/studentService';
import { Guardian } from '../../types/student';

const GuardiansCard: React.FC = () => {
  const [guardians, setGuardians] = useState<Guardian[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unlinking, setUnlinking] = useState<number | null>(null);

  useEffect(() => {
    fetchGuardians();
  }, []);

  const fetchGuardians = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMyGuardians();
      setGuardians(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load guardians';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlink = async (relationshipId: number, guardianName: string) => {
    if (!window.confirm(`Are you sure you want to unlink from ${guardianName}? This action cannot be undone.`)) {
      return;
    }

    setUnlinking(relationshipId);
    setError(null);
    try {
      await unlinkGuardian(relationshipId);
      // Remove from local state
      setGuardians(guardians.filter(g => g.relationshipId !== relationshipId));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to unlink';
      setError(errorMessage);
    } finally {
      setUnlinking(null);
    }
  };

  const getIcon = (type: Guardian['relationshipType']) => {
    switch (type) {
      case 'teacher':
        return <TeacherIcon color="primary" />;
      case 'parent':
        return <ParentIcon color="secondary" />;
      case 'coach':
        return <CoachIcon color="success" />;
      case 'emergency_contact':
        return <EmergencyIcon color="error" />;
      default:
        return <GuardianIcon />;
    }
  };

  const getTypeLabel = (type: Guardian['relationshipType']) => {
    return type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Card elevation={2}>
      <CardHeader
        title="My Teachers & Guardians"
        titleTypographyProps={{ variant: 'h6' }}
      />
      <CardContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : guardians.length === 0 ? (
          <Box textAlign="center" py={3}>
            <Typography variant="body2" color="text.secondary">
              No teachers or guardians linked to your account yet
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {guardians.map((guardian, index) => (
              <React.Fragment key={guardian.relationshipId}>
                {index > 0 && <Divider />}
                <ListItem
                  sx={{ px: 0 }}
                  secondaryAction={
                    <Tooltip title="Unlink">
                      <IconButton
                        edge="end"
                        onClick={() =>
                          handleUnlink(
                            guardian.relationshipId,
                            `${guardian.guardianFirstName} ${guardian.guardianLastName}`
                          )
                        }
                        disabled={unlinking === guardian.relationshipId}
                        size="small"
                      >
                        {unlinking === guardian.relationshipId ? (
                          <CircularProgress size={20} />
                        ) : (
                          <UnlinkIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemIcon>{getIcon(guardian.relationshipType)}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body1">
                          {guardian.guardianFirstName} {guardian.guardianLastName}
                        </Typography>
                        {guardian.isPrimary && (
                          <Chip label="Primary" size="small" color="primary" variant="outlined" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Typography variant="body2" color="text.secondary">
                          {guardian.guardianEmail}
                        </Typography>
                        <Chip
                          label={getTypeLabel(guardian.relationshipType)}
                          size="small"
                          variant="outlined"
                          sx={{ width: 'fit-content' }}
                        />
                      </Box>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default GuardiansCard;
